import { getApiClient } from '../axios-config';

/**
 * Location API Service
 * Handles all API calls related to locations
 */
class LocationsAPI {
  /**
   * Get all locations
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of location objects
   */
  static async getAll(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/locations/getlocations');
    return response.data.data.locations;
  }

  /**
   * Get location by ID
   * @param {string} id - Location ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Location object
   */
  static async getById(id, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/locations/${id}`);
    return response.data.data.location;
  }

  /**
   * Create a new location
   * @param {Object} location - Location object with name and description
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created location object
   */
  static async create(location, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/locations/addlocation', {
      newLocation: location,
    });
    return response.data.data.location || location; // Return created location or the one we sent
  }

  /**
   * Update an existing location
   * @param {Object} location - Location object with id, name, and description
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated location object
   */
  static async update(location, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/locations/modifylocation', {
      updatedLocation: location,
    });
    return response.data.data.modifiedLocation || location; // Return updated location or the one we sent
  }

  /**
   * Delete a location
   * @param {string} id - Location ID to delete
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async delete(id, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete('/api/locations/deletelocation', {
      params: {
        deletedLocationId: id,
      },
    });
  }
}

export default LocationsAPI;
